import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import ModalRegisterOptions from './modals/ModalRegisterOptions';
import UserMenuModal from './modals/UserMenuModal';
import EnableNotificationsButton from './EnableNotificationsButton';
import ModalSelectCompany from './modals/ModalSelectCompany';
import ModalEditProfile from './modals/ModalEditProfile';

import { CircleUser, LogOut, PlusIcon, Building2, RefreshCcw } from 'lucide-react';
import Avatar from './Avatar';
import { getCompany, getCompanyById, logoutAttendant } from '../api/api';
import { clearCompanyScopedCaches } from '../utils/cache.js';
import { getAuthSession, performLogout, setAuthSession } from '../utils/auth';
import ConfirmDialog from './common/ConfirmDialog.jsx';
import { attendantHasPermission } from '../utils/role.js';
import { useChatContext } from '../context/useChatContext.js';
import { INTERNAL_CHAT_HEADER_SLOT_ID } from '../utils/internalChatLauncher.js';

import './Header.css';

const MEDIA_BASE = import.meta.env.VITE_MEDIA_BASE ?? '/media';
const DEFAULT_HEADER_HEIGHT_CSS = '70px';

const resolveMediaUrl = (src) => {
  if (!src) return '';
  const s = String(src).trim();
  if (/^(data:|blob:|file:|https?:\/\/)/i.test(s)) return s;
  if (s.startsWith('/')) return s;
  const base = MEDIA_BASE.replace(/\/$/, '');
  const path = s.replace(/^\//, '').replace(/^media\//, '');
  return `${base}/${path}`;
};

function Header({ collapsed, isMobile = false }) {
  const navigate = useNavigate();
  const OFFLINE_REFRESH_DELAY_MS = 4000;
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [attendantName, setAttendantName] = useState('');
  const [attendantPhoto, setAttendantPhoto] = useState('');
  const [attendantEmail, setAttendantEmail] = useState('');
  const [attendantProfile, setAttendantProfile] = useState(null);
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [showSessionRefresh, setShowSessionRefresh] = useState(false);
  const registerRef = useRef(null);
  const headerRef = useRef(null);
  const offlineTimerRef = useRef(null);
  const {
    attendantData,
    isSuperAdmin,
    isGlobalAdmin,
    companyId,
    companySettings,
    isSessionOffline,
    isSessionInvalid,
    refreshSession,
  } = useChatContext();

  const normalizeAttendantProfile = (data = {}) => {
    const normalizedId =
      data?.id ??
      data?.attendant_id ??
      data?.attendantId ??
      data?.user_id ??
      null;
    const normalizedName =
      data?.name ||
      data?.attendant_name ||
      data?.attendantName ||
      data?.user?.name ||
      data?.profile?.name ||
      '';
    const normalizedEmail = data?.email || data?.user?.email || data?.profile?.email || '';
    const normalizedPhoto =
      data?.photograph ||
      data?.photo ||
      data?.avatar ||
      data?.user?.photograph ||
      data?.user?.photo ||
      data?.profile?.photo ||
      '';
    const activeSound =
      data?.active_sound_notification ??
      data?.activeSoundNotification ??
      data?.notificationsSound ??
      undefined;
    const activePopup =
      data?.active_popup_notification ??
      data?.activePopupNotification ??
      data?.notificationsPopup ??
      undefined;

    return {
      ...data,
      id: normalizedId,
      name: normalizedName,
      email: normalizedEmail,
      photograph: normalizedPhoto,
      active_sound_notification: activeSound,
      active_popup_notification: activePopup,
    };
  };

  const applyAttendantProfile = useCallback((rawProfile) => {
    const normalized = normalizeAttendantProfile(rawProfile || {});
    setAttendantProfile(normalized);
    setAttendantName(normalized?.name || '');
    setAttendantEmail(normalized?.email || '');
    setAttendantPhoto(normalized?.photograph ? resolveMediaUrl(normalized.photograph) : '');
    return normalized;
  }, []);

  const performLogoutFlow = () => {
    logoutAttendant().catch(() => {
      // Silencia falhas: se o token já expirou, tratamos como logout mesmo assim
    });

    performLogout();
    setUserMenuOpen(false);
    // Redireciona via router (sem usar window.location) e deixa PrivateRoute bloquear o restante
    navigate('/login', { replace: true });
  };

  const handleLogout = () => setConfirmLogoutOpen(true);
  const handleConfirmLogout = () => {
    setConfirmLogoutOpen(false);
    performLogoutFlow();
  };

  const hasPermission = useCallback(
    (slug) => attendantHasPermission(attendantData, slug),
    [attendantData]
  );

  const canSelectCompany = useMemo(() => isGlobalAdmin, [isGlobalAdmin]);
  const canManageCompanies = useMemo(() => isSuperAdmin, [isSuperAdmin]);
  const canCreateAttendant = useMemo(
    () => Boolean(companyId) && (hasPermission('attendants:manage') || isSuperAdmin),
    [companyId, hasPermission, isSuperAdmin]
  );
  const canCreateContact = useMemo(
    () => Boolean(companyId) && (hasPermission('contacts:manage') || isSuperAdmin),
    [companyId, hasPermission, isSuperAdmin]
  );
  const allowRegister = canCreateAttendant || canCreateContact || canManageCompanies;

  useEffect(() => {
    if (attendantData) {
      applyAttendantProfile(attendantData);
    }
  }, [applyAttendantProfile, attendantData]);

  useEffect(() => {
    let active = true;
    const loadCompany = async () => {
      if (!companyId) {
        setCompanyName('');
        return;
      }
      if (typeof companySettings?.name === 'string' && companySettings.name.trim()) {
        setCompanyName(companySettings.name.trim());
        return;
      }
      try {
        const company = await getCompanyById(companyId);
        if (!active) return;
        setCompanyName(company?.name || '');
      } catch (err) {
        console.error('Erro ao buscar dados da empresa:', err);
      }
    };
    loadCompany();
    return () => {
      active = false;
    };
  }, [companyId, companySettings?.name]);

  const handleCompanyClick = async () => {
    if (!canSelectCompany) return;
    if (!isCompanyOpen) {
      try {
        const data = await getCompany();
        const activeCompanies = Array.isArray(data)
          ? data.filter((company) => company?.active)
          : [];
        setCompanies(activeCompanies);
      } catch (err) {
        console.error('Erro ao buscar empresas:', err);
      }
    }
    setIsCompanyOpen(!isCompanyOpen);
  };

  const handleSelectCompany = (company) => {
    const rawCompanyId = company?.id;
    const normalizedCompanyId =
      rawCompanyId === null || rawCompanyId === undefined ? null : Number(rawCompanyId);
    if (
      normalizedCompanyId !== null &&
      (!Number.isFinite(normalizedCompanyId) || normalizedCompanyId <= 0)
    ) {
      return;
    }
    try {
      clearCompanyScopedCaches();
    } catch (err) {
      console.warn('Falha ao resetar caches ao trocar de empresa:', err);
    }
    try {
      setAuthSession({ companyId: normalizedCompanyId });
      const snapshot = getAuthSession();
      window.dispatchEvent(
        new CustomEvent('bluezap:auth-changed', {
          detail: {
            attendantId: snapshot.attendantId ?? null,
            companyId: snapshot.companyId ?? null,
            roleName: snapshot.roleName ?? null,
            roleId: snapshot.roleId ?? null,
          },
        })
      );
    } catch (err) {
      console.warn('Falha ao atualizar sessão ao trocar de empresa:', err);
    }

    setCompanyName(normalizedCompanyId === null ? '' : company?.name || '');
    setIsCompanyOpen(false);
  };

  const handleEditProfile = () => {
    setIsEditProfileOpen(true);
    setUserMenuOpen(false);
  };

  const handleProfileUpdated = (updatedProfile) => {
    const normalized = applyAttendantProfile(updatedProfile);
    if (normalized?.id != null) {
      localStorage.setItem('attendant_id', normalized.id);
    }
  };

  // trava o scroll do body quando o modal de empresas estiver aberto (opcional)
  useEffect(() => {
    if (!isCompanyOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isCompanyOpen]);

  const sessionIndicator = useMemo(() => {
    if (isSessionInvalid) {
      return {
        tone: 'invalid',
        label: 'Sessão inválida',
        tooltip: 'Sessão expirada. Faça login novamente.',
      };
    }
    if (isSessionOffline) {
      return {
        tone: 'offline',
        label: 'Offline',
        tooltip: 'Sessão não verificada. Verifique sua conexão.',
      };
    }
    return null;
  }, [isSessionInvalid, isSessionOffline]);

  const [isSessionRefreshing, setIsSessionRefreshing] = useState(false);
  const handleRefreshSession = useCallback(async () => {
    if (isSessionRefreshing) return;
    setIsSessionRefreshing(true);
    try {
      await refreshSession();
    } finally {
      setIsSessionRefreshing(false);
    }
  }, [isSessionRefreshing, refreshSession]);

  useEffect(() => {
    if (!isSessionOffline) {
      setShowSessionRefresh(false);
      if (offlineTimerRef.current) {
        clearTimeout(offlineTimerRef.current);
        offlineTimerRef.current = null;
      }
      return undefined;
    }

    setShowSessionRefresh(false);
    if (offlineTimerRef.current) {
      clearTimeout(offlineTimerRef.current);
    }
    offlineTimerRef.current = setTimeout(() => {
      setShowSessionRefresh(true);
    }, OFFLINE_REFRESH_DELAY_MS);

    return () => {
      if (offlineTimerRef.current) {
        clearTimeout(offlineTimerRef.current);
        offlineTimerRef.current = null;
      }
    };
  }, [isSessionOffline, OFFLINE_REFRESH_DELAY_MS]);

  useEffect(() => {
    const headerEl = headerRef.current;
    if (!headerEl || typeof window === 'undefined') return undefined;

    const root = document.documentElement;
    let frameId = 0;

    const updateHeaderHeightVar = () => {
      const headerHeight = Math.ceil(headerEl.getBoundingClientRect().height);
      if (!Number.isFinite(headerHeight) || headerHeight <= 0) return;
      root.style.setProperty('--header-h', `${headerHeight}px`);
    };

    const scheduleUpdate = () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateHeaderHeightVar);
    };

    updateHeaderHeightVar();

    const observer =
      typeof window.ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleUpdate)
        : null;
    observer?.observe(headerEl);

    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('orientationchange', scheduleUpdate);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('orientationchange', scheduleUpdate);
      if (frameId) window.cancelAnimationFrame(frameId);
      root.style.setProperty('--header-h', DEFAULT_HEADER_HEIGHT_CSS);
    };
  }, []);

  const headerClass = `header ${isMobile ? 'header--mobile' : collapsed ? 'header--collapsed' : 'header--expanded'}`;
  const logoClass = `logo-img ${collapsed ? 'logo-img--compact' : ''}`;

  // onde vamos portalar (body ou #modal-root se existir)
  const modalHost = document.getElementById('modal-root') || document.body;

  return (
    <header ref={headerRef} className={headerClass}>
      <div className="header-inner">
        {/* LOGO / TÍTULO À ESQUERDA */}
        <div className="header-logo">
          <img src="/logo.png" alt="Logo BlueZap" className={logoClass} />
          <span className="logo-text">BlueZap</span>
        </div>

        {/* AÇÕES À DIREITA */}
        <div className="header-actions">
          <div id={INTERNAL_CHAT_HEADER_SLOT_ID} className="header-chat-slot" />

          <EnableNotificationsButton collapsed={collapsed} />

          {sessionIndicator && (
            <div
              className={`session-indicator session-indicator--${sessionIndicator.tone}`}
              role="status"
              title={sessionIndicator.tooltip}
              aria-label={sessionIndicator.tooltip}
            >
              <i className="session-indicator__dot" />
              <span className="visually-hidden">{sessionIndicator.label}</span>
            </div>
          )}

          {isSessionOffline && showSessionRefresh && (
            <button
              type="button"
              className="session-refresh"
              onClick={handleRefreshSession}
              disabled={isSessionRefreshing}
              title="Recarregar sessão"
              aria-label="Recarregar sessão"
            >
              <RefreshCcw className={`icon ${isSessionRefreshing ? 'spin' : ''}`} />
              <span className="visually-hidden">Recarregar sessão</span>
            </button>
          )}

          {canSelectCompany && (
            <>
              <button
                onClick={handleCompanyClick}
                type="button"
                className="header-button"
                title={collapsed ? (companyName || 'Empresas') : undefined}
                aria-label={collapsed ? (companyName || 'Empresas') : undefined}
              >
                <Building2 className="icon" />
                <span>{companyName || 'Empresas'}</span>
              </button>

              {/* PORTAL: renderiza o modal FORA do header */}
              {isCompanyOpen && createPortal(
                <ModalSelectCompany
                  open={isCompanyOpen}
                  onClose={() => setIsCompanyOpen(false)}
                  companies={companies}
                  onSelect={handleSelectCompany}
                />,
                modalHost
              )}
            </>
          )}

          {allowRegister && (
            <>
              <button
                ref={registerRef}
                onClick={() => setIsRegisterOpen(!isRegisterOpen)}
                type="button"
                title={collapsed ? 'Cadastrar' : undefined}
                aria-label={collapsed ? 'Cadastrar' : undefined}
              >
                <PlusIcon className="icon" />
                <span>Cadastrar</span>
              </button>

              <ModalRegisterOptions
                isOpen={isRegisterOpen}
                onClose={() => setIsRegisterOpen(false)}
                buttonRef={registerRef}
                canCreateAttendant={canCreateAttendant}
                canCreateContact={canCreateContact}
                canCreateCompany={canManageCompanies}
              />
            </>
          )}

          <div className="user-menu">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              title={collapsed ? (attendantName || 'Usuário') : undefined}
              aria-label={collapsed ? (attendantName || 'Usuário') : undefined}
            >
              {attendantPhoto || attendantName ? (
                <Avatar name={attendantName} photo={attendantPhoto} size={28} />
              ) : (
                <CircleUser className="icon" />
              )}
              <span>{attendantName || 'Usuário'}</span>
            </button>

            <UserMenuModal
              open={userMenuOpen}
              onClose={() => setUserMenuOpen(false)}
              onEditProfile={handleEditProfile}
              user={{
                ...(attendantProfile || {}),
                name: attendantName,
                email: attendantEmail,
                avatarUrl: attendantPhoto,
              }}
            />
          </div>

          <button
            type="button"
            onClick={handleLogout}
            title={collapsed ? 'Sair' : undefined}
            aria-label={collapsed ? 'Sair' : undefined}
          >
            <LogOut className="icon" />
            <span>Sair</span>
          </button>
        </div>
      </div>

      <ModalEditProfile
        open={isEditProfileOpen}
        onClose={() => setIsEditProfileOpen(false)}
        user={
          attendantProfile || {
            name: attendantName,
            email: attendantEmail,
            photograph: attendantPhoto,
          }
        }
        onUpdated={handleProfileUpdated}
        container={modalHost}
      />

      <ConfirmDialog
        open={confirmLogoutOpen}
        title="Sair"
        message="Tem certeza de que deseja sair da sua conta?"
        confirmText="Sair"
        cancelText="Cancelar"
        variant="danger"
        onConfirm={handleConfirmLogout}
        onCancel={() => setConfirmLogoutOpen(false)}
      />
    </header>

  );
}

export default Header;
